import {useCallback} from 'react';

import {FAILED_AUTH_INDEX} from '@web-catalog/const/numbers';

import {AppType} from '@web-catalog/types';

import {useAuthorizedProfile} from '@web-catalog/store/auth';

import {emitYmEvent, logTmrEvent} from '@web-catalog/utils/analytics/logEvent';

const appTypeToCatalogCategoryEventSuffix: Record<AppType, string> = {
  [AppType.GAMES]: 'games',
  [AppType.MAIN]: 'apps',
};

export const useBaseAnalytics = () => {
  const userInfo = useAuthorizedProfile();

  const genericBackClick = () => {
    emitYmEvent({eventName: 'button_back'});
  };

  const logBackClick = useCallback(
    (appId: string, appType?: AppType) => {
      if (appType === AppType.MAIN) {
        logTmrEvent({
          logEvent: 'appPage.back.click',
          params: {
            app_id: appId,
            vk_id: userInfo?.id ?? FAILED_AUTH_INDEX,
          },
        });
      }

      if (appType === AppType.GAMES) {
        logTmrEvent({
          logEvent: 'gamePage.back.click',
          params: {
            app_id: appId,
            vk_id: userInfo?.id ?? FAILED_AUTH_INDEX,
          },
        });
      }

      emitYmEvent({
        eventName: 'detail_button_back',
        params: {app_id_311741867: appId},
      });
    },
    [userInfo?.id],
  );

  const logDeveloperAppsBackClick = useCallback(genericBackClick, []);

  const logReviewsBackClick = useCallback(genericBackClick, []);

  const logRecommendationsBackClick = useCallback(genericBackClick, []);

  const logScreenshotSwipeLeftSwipe = useCallback(
    (appId: string) => {
      logTmrEvent({
        logEvent: 'screenshotSwipeLeft.swipe',
        params: {
          app_id: appId,
          vk_id: userInfo?.id ?? FAILED_AUTH_INDEX,
        },
      });
    },
    [userInfo?.id],
  );
  const logScreenshotSwipeRightSwipe = useCallback(
    (appId: string) => {
      logTmrEvent({
        logEvent: 'screenshotSwipeRight.swipe',
        params: {
          app_id: appId,
          vk_id: userInfo?.id ?? FAILED_AUTH_INDEX,
        },
      });
    },
    [userInfo?.id],
  );
  const logAppsView = useCallback(() => {
    logTmrEvent({
      logEvent: 'AppsWithPagination.view',
      params: {
        vk_id: userInfo?.id ?? FAILED_AUTH_INDEX,
      },
    });
  }, [userInfo?.id]);

  const logAppCategoryView = useCallback(
    (appCategoryName: string) => {
      logTmrEvent({
        logEvent: 'appCategory.view',
        params: {
          app_category_name: appCategoryName,
          vk_id: userInfo?.id ?? FAILED_AUTH_INDEX,
        },
      });
    },
    [userInfo?.id],
  );
  const logGamesView = useCallback(() => {
    logTmrEvent({
      logEvent: 'games.view',
      params: {
        vk_id: userInfo?.id ?? FAILED_AUTH_INDEX,
      },
    });
  }, [userInfo?.id]);

  const logGameCategoryView = useCallback(
    (appCategoryName: string) => {
      logTmrEvent({
        logEvent: 'gameCategory.view',
        params: {
          app_category_name: appCategoryName,
          vk_id: userInfo?.id ?? FAILED_AUTH_INDEX,
        },
      });
    },
    [userInfo?.id],
  );

  const logCatalogCategoryClick = useCallback(
    (appType: AppType, alreadyActive: boolean) => {
      if (!alreadyActive) {
        emitYmEvent({
          eventName: `catalog_sidebar_${appTypeToCatalogCategoryEventSuffix[appType]}`,
        });
      }
    },
    [],
  );

  const logSearchPageView = useCallback(
    (pageNumber: number) => {
      logTmrEvent({
        logEvent: 'search.view',
        params: {
          page_num: pageNumber,
          vk_id: userInfo?.id ?? FAILED_AUTH_INDEX,
        },
      });
    },
    [userInfo?.id],
  );
  const logSearchPageBackClick = useCallback(() => {
    logTmrEvent({
      logEvent: 'search.back.click',
      params: {
        vk_id: userInfo?.id ?? FAILED_AUTH_INDEX,
      },
    });
  }, [userInfo?.id]);

  return {
    logBackClick,
    logDeveloperAppsBackClick,
    logReviewsBackClick,
    logRecommendationsBackClick,
    logScreenshotSwipeLeftSwipe,
    logScreenshotSwipeRightSwipe,
    logAppsView,
    logAppCategoryView,
    logGamesView,
    logGameCategoryView,
    logCatalogCategoryClick,
    logSearchPageView,
    logSearchPageBackClick,
  };
};
